// =============================================================================
// Roll Engine — Phase A + Phase C (client-side)
// See frontend/src/engine/diceParser.ts for dice expression parsing.
//
// Critical hit rules by game system
// ─────────────────────────────────
// D&D 3.5 / PF1 (and custom):
//   Roll the ENTIRE damage expression (dice + modifier) critMult times.
//   critMult is per damage row — set it to 1 to suppress multiplication
//   for any row that should not be multiplied (e.g. sneak attack, energy rider).
//     2d6+15  critMult=2 → 2×(2d6+15) = 4d6+30
//     2d4+5   critMult=4 → 4×(2d4+5)  = 8d4+20
//
// D&D 5e:
//   Only the DICE are doubled; flat modifiers are added once.
//   critMult = 1 still suppresses multiplication for that row.
//     1d8+4   crit        → 2d8+4
//
// critMult = 1 (suppress crit multiplication):
//   The row is still applied on a crit but its value is not multiplied.
//   Use this for damage that the rules explicitly exclude from the multiplier
//   (e.g. flaming burst fire dice, sneak attack, etc.).
// =============================================================================
import { parseDiceExpr } from './diceParser';
import { parseAttackModSource, resolveAttackModSource, resolveComponents } from './statResolver';
import { adjustDiceForSizeChange } from './sizeProgression';
import type { Character } from '../../../backend/shared/types/index';
import type {
  AttackNode, DamageNode, ConditionalBonusDamageNode, ConditionalDamageRollResult,
  AttackRollResult, DamageRollResult,
  PendingRollResult, CompletedRollResult, ResolvedAttackResult,
  AttackRoutine, RollMode, SpecialDamageType, WeaponSpecialEffectResult,
} from '../../../backend/shared/types/index';

// ---------------------------------------------------------------------------
// Die roller
// ---------------------------------------------------------------------------

function rollDie(sides: number): number {
  return Math.floor(Math.random() * sides) + 1;
}

function rollDamageNode(
  node:    DamageNode,
  extraMod = 0,
  char?:   Character | null,
): DamageRollResult {
  const parsed = parseDiceExpr(node.diceExpr) ?? { diceCount: 0, diceSize: 0, modifier: 0 };
  const rolls  = parsed.diceCount > 0
    ? Array.from({ length: parsed.diceCount }, () => rollDie(parsed.diceSize))
    : [];

  // Resolve modifier components (Phase 3+); fall back to embedded flat mod (Phase 2).
  const legacyFlat = parsed.modifier + extraMod;
  const { total: modTotal, resolved: resolvedMods } = node.modComponents?.length
    ? resolveComponents(node.modComponents, char, legacyFlat)
    : { total: legacyFlat, resolved: [] };

  const total = rolls.reduce((s, r) => s + r, 0) + modTotal;
  return {
    nodeId:      node.id,
    diceExpr:    node.diceExpr,
    rolls,
    modifier:    modTotal,
    total,
    notes:       node.notes,
    critMult:    node.critMult,
    specialType: node.specialType,
    resolvedModComponents: resolvedMods.length ? resolvedMods : undefined,
  };
}

function maxDiceTotal(node: DamageRollResult): number {
  const parsed = parseDiceExpr(node.diceExpr);
  return parsed && parsed.diceCount > 0
    ? parsed.diceCount * parsed.diceSize
    : node.rolls.reduce((s, r) => s + r, 0);
}

function perfectCritTotal(node: DamageRollResult): number {
  return maxDiceTotal(node) + node.rolls.reduce((s, r) => s + r, 0) + node.modifier;
}

// ---------------------------------------------------------------------------
// Size scaling helper
// ---------------------------------------------------------------------------

/** Return a copy of node with diceExpr adjusted for size steps (if weaponSize is set). */
function sizeScaledNode(node: DamageNode, sizeSteps: number): DamageNode {
  if (!node.weaponSize || sizeSteps === 0) return node;
  return { ...node, diceExpr: adjustDiceForSizeChange(node.diceExpr, node.weaponSize, sizeSteps) };
}

type AttackValueResolver = number | ((attack: AttackNode) => number);
type AttackRollModeResolver = RollMode | ((attack: AttackNode) => RollMode);

function resolveAttackValue(resolver: AttackValueResolver, attack: AttackNode): number {
  return typeof resolver === 'function' ? resolver(attack) : resolver;
}

function resolveAttackRollMode(resolver: AttackRollModeResolver, attack: AttackNode): RollMode {
  return typeof resolver === 'function' ? resolver(attack) : resolver;
}

const SHARPNESS_LIMBS = ['left arm', 'right arm', 'left leg', 'right leg'];

function rollSharpnessLimb(): string {
  return SHARPNESS_LIMBS[rollDie(SHARPNESS_LIMBS.length) - 1];
}

function sharpnessEligibility(naturalRoll: number): string {
  if (naturalRoll >= 20) return 'can sever Medium or smaller, Large or larger, and solid metal or stone limbs';
  if (naturalRoll >= 19) return 'can sever Medium or smaller and Large or larger creature limbs';
  return 'can sever limbs of creatures up to Medium size';
}

function resolveWeaponSpecialEffects(atk: AttackRollResult, isHit: boolean): WeaponSpecialEffectResult[] | undefined {
  if (!isHit || !atk.weaponProperties?.length) return undefined;

  const effects: WeaponSpecialEffectResult[] = [];
  if (atk.weaponProperties.includes('vorpal') && atk.d20 === 20) {
    effects.push({
      property:    'vorpal',
      naturalRoll: atk.d20,
      effect:      "target loses its head",
    });
  }

  if (atk.weaponProperties.includes('sharpness') && atk.d20 >= 18) {
    effects.push({
      property:    'sharpness',
      naturalRoll: atk.d20,
      effect:      'severs a non-head limb',
      limb:        rollSharpnessLimb(),
      eligibility: sharpnessEligibility(atk.d20),
    });
  }

  return effects.length ? effects : undefined;
}

// ---------------------------------------------------------------------------
// Phase A — Roll
// ---------------------------------------------------------------------------

/**
 * Roll all attacks and pre-roll all damage dice.
 *
 * @param routine              The attack routine to execute.
 * @param attackBonus          Flat bonus to every attack roll from active campaign modifiers.
 * @param damageBonus          Flat bonus to the first damage node of every attack.
 * @param appliedModifierIds   Campaign modifier IDs that contributed to the bonuses.
 * @param char                 Optional live character — used to resolve stat references.
 * @param rollMode             normal / advantage / disadvantage.
 * @param sizeSteps            Steps to shift size for all damage nodes with weaponSize set.
 * @param appliedModifierNames Human-readable names for each modifier in appliedModifierIds,
 *                             in the same order.  Included in VTT dispatch cards.
 * @param charName             Character name for the Phase A VTT card header.
 */
export function rollPhaseA(
  routine:              AttackRoutine,
  attackBonus:          AttackValueResolver = 0,
  damageBonus:          AttackValueResolver = 0,
  appliedModifierIds:   string[] = [],
  /** Optional live character — used to resolve stat references in attack modifiers. */
  char?:                Character | null,
  rollMode:             AttackRollModeResolver = 'normal',
  /** Steps to shift size for all damage nodes that have weaponSize set. */
  sizeSteps             = 0,
  appliedModifierNames: string[] = [],
  charName?:            string,
): PendingRollResult {
  const attackResults: AttackRollResult[] = [];
  const routineRollMode: RollMode = typeof rollMode === 'function' ? 'normal' : rollMode;

  for (const node of routine.definition) {
    if (node.type !== 'attack') continue;
    const attack = node as AttackNode;
    const attackRollMode = resolveAttackRollMode(rollMode, attack);
    const attackFlatBonus = resolveAttackValue(attackBonus, attack);
    const damageFlatBonus = resolveAttackValue(damageBonus, attack);

    // Advantage/Disadvantage: roll a second d20 and pick higher/lower.
    const d20a    = rollDie(20);
    const d20b    = attackRollMode !== 'normal' ? rollDie(20) : undefined;
    const d20     = attackRollMode === 'advantage'
      ? Math.max(d20a, d20b!)
      : attackRollMode === 'disadvantage'
        ? Math.min(d20a, d20b!)
        : d20a;
    // Resolve attack modifier: use attackComponents (Phase 3+) when present,
    // otherwise fall back to attackModSource / attackMod (Phase 2 compat).
    let effectiveMod = attackFlatBonus;
    let resolvedAttackComponents: import('../../../backend/shared/types/index').ResolvedModComponent[] | undefined;

    if (attack.attackComponents?.length) {
      const { total, resolved } = resolveComponents(attack.attackComponents, char, 0, routine.gameSystem);
      effectiveMod += total;
      resolvedAttackComponents = resolved.length ? resolved : undefined;
    } else {
      const baseAttackMod = attack.attackModSource
        ? resolveAttackModSource(parseAttackModSource(attack.attackModSource, attack.attackMod), char)
        : attack.attackMod;
      effectiveMod += baseAttackMod;
    }
    const total        = d20 + effectiveMod;
    // noCrit suppresses critical hits entirely (e.g. grapple/CMB checks, skill rolls).
    const isThreat     = !attack.noCrit
      && d20 >= attack.critRange[0]
      && d20 <= attack.critRange[1];

    // Filter out empty placeholder rows (blank diceExpr, no mod components).
    // These are unfinished rows from presets that haven't been filled in yet.
    // Applying the campaign damage bonus to them would produce phantom damage
    // purely from the modifier — no dice means no damage, even with buffs active.
    const effectiveDamage = attack.damage.filter(
      (d) => d.diceExpr.trim() !== '' || (d.modComponents?.length ?? 0) > 0,
    );

    // Roll on-hit damage rows; apply campaign damageBonus to the first filled row only.
    // Apply size scaling to any row that has weaponSize set.
    const damage = effectiveDamage.map((d, i) =>
      rollDamageNode(sizeScaledNode(d, sizeSteps), i === 0 ? damageFlatBonus : 0, char),
    );

    // Pre-roll on-crit-extra rows — also size-scaled.
    const onCritExtraDamage = attack.onCritExtra.map((d) => rollDamageNode(sizeScaledNode(d, sizeSteps), 0, char));

    attackResults.push({
      nodeId:    attack.id,
      label:     attack.label,
      d20,
      attackMod: effectiveMod,
      total,
      vsTarget:  attack.vsTarget,
      resolvedAttackComponents,
      isThreat,
      isCrit:       isThreat,  // Phase 2: auto-confirm; Phase 3 adds confirmation roll
      rollMode: attackRollMode,
      advantageDie: d20b,
      damage,
      onCritExtraDamage,
      critStyle: attack.critStyle,  // carry through for Phase C calculation
      weaponProperties: !attack.noCrit && attack.weaponProperties?.length ? attack.weaponProperties : undefined,
    });
  }

  // Pre-roll conditional bonus damage — dice are always rolled;
  // whether they count is gated in Phase C by condition evaluation.
  const conditionalDamageResults: ConditionalDamageRollResult[] = routine.definition
    .filter((n): n is ConditionalBonusDamageNode => n.type === 'conditional_damage')
    .map((node) => ({
      nodeId:    node.id,
      label:     node.label,
      condition: node.condition,
      triggered: false,   // evaluated in Phase C
      damage:    node.damage
        .filter((d) => d.diceExpr.trim() !== '' || (d.modComponents?.length ?? 0) > 0)
        .map((d) => rollDamageNode(sizeScaledNode(d, sizeSteps), 0, char)),
      total: 0,
    }));

  // Phase 5: Pre-roll bonus attacks.
  // 'hits' type: pre-roll one attack in Phase A (re-used in Phase C).
  // 'crit_hits' type: no pre-roll — attacks are generated in Phase C from
  //   the stored nodeDefinition, once the actual crit count is known.
  const bonusAttackResults: any[] = [];
  for (const node of routine.definition) {
    if (node.type !== 'bonus_attack') continue;
    const bonusNode = node as any; // BonusAttackNode

    if (bonusNode.condition.subject === 'crit_hits') {
      // Defer all rolling to Phase C; just record the definition.
      bonusAttackResults.push({
        nodeId:         bonusNode.id,
        label:          bonusNode.label,
        condition:      bonusNode.condition,
        nodeDefinition: bonusNode,
        triggered:      false,
        attackResults:  [],
      });
      continue;
    }

    // 'hits' type — pre-roll one attack now
    const attack = bonusNode.attack as AttackNode;
    const attackRollMode = resolveAttackRollMode(rollMode, attack);
    const attackFlatBonus = resolveAttackValue(attackBonus, attack);
    const damageFlatBonus = resolveAttackValue(damageBonus, attack);
    const d20a = rollDie(20);
    const d20b = attackRollMode !== 'normal' ? rollDie(20) : undefined;
    const d20 = attackRollMode === 'advantage'
      ? Math.max(d20a, d20b!)
      : attackRollMode === 'disadvantage'
        ? Math.min(d20a, d20b!)
        : d20a;

    let effectiveMod = attackFlatBonus;
    let resolvedAttackComponents: any[] | undefined;

    if (attack.attackComponents?.length) {
      const { total, resolved } = resolveComponents(attack.attackComponents, char, 0, routine.gameSystem);
      effectiveMod += total;
      resolvedAttackComponents = resolved.length ? resolved : undefined;
    } else {
      const baseAttackMod = attack.attackModSource
        ? resolveAttackModSource(parseAttackModSource(attack.attackModSource, attack.attackMod), char)
        : attack.attackMod;
      effectiveMod += baseAttackMod;
    }

    const total = d20 + effectiveMod;
    const isThreat = !attack.noCrit
      && d20 >= attack.critRange[0]
      && d20 <= attack.critRange[1];

    const effectiveDamage = attack.damage.filter(
      (d: any) => d.diceExpr.trim() !== '' || (d.modComponents?.length ?? 0) > 0,
    );

    const attackResult = {
      nodeId: attack.id,
      label: attack.label,
      d20,
      attackMod: effectiveMod,
      total,
      vsTarget: attack.vsTarget,
      resolvedAttackComponents,
      isThreat,
      isCrit: isThreat,
      rollMode: attackRollMode,
      advantageDie: d20b,
      damage: effectiveDamage.map((d: any) => rollDamageNode(sizeScaledNode(d, sizeSteps), damageFlatBonus, char)),
      onCritExtraDamage: attack.onCritExtra
        .filter((d: any) => d.diceExpr.trim() !== '' || (d.modComponents?.length ?? 0) > 0)
        .map((d: any) => rollDamageNode(sizeScaledNode(d, sizeSteps), damageFlatBonus, char)),
      critStyle: attack.critStyle,
      weaponProperties: !attack.noCrit && attack.weaponProperties?.length ? attack.weaponProperties : undefined,
    };

    bonusAttackResults.push({
      nodeId:         bonusNode.id,
      label:          bonusNode.label,
      condition:      bonusNode.condition,
      nodeDefinition: bonusNode,
      triggered:      false,
      attackResults:  [attackResult],
    });
  }

  return {
    routineId:             routine.routineId,
    routineName:           routine.name,
    characterName:         charName,
    gameSystem:            routine.gameSystem,
    rollMode:              routineRollMode,
    attackResults,
    conditionalDamageResults,
    bonusAttackResults:    bonusAttackResults.length ? bonusAttackResults : undefined,
    appliedModifierIds,
    appliedModifierNames:  appliedModifierNames.length ? appliedModifierNames : undefined,
    rolledAt:              Date.now(),
  };
}

// ---------------------------------------------------------------------------
// Phase C — Resolution
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Bonus attack roller — rolls one fresh AttackRollResult from a BonusAttackNode
// definition. Used by the executor to generate interactive bonus-round attacks.
// ---------------------------------------------------------------------------
export function rollOneBonusAttack(
  attack:      AttackNode,
  attackBonus: AttackValueResolver,
  damageBonus: AttackValueResolver,
  char?:       Character | null,
  gameSystem?: AttackRoutine['gameSystem'],
  rollMode:    RollMode = 'normal',
): AttackRollResult {
  const d20a = rollDie(20);
  const d20b = rollMode !== 'normal' ? rollDie(20) : undefined;
  const d20 = rollMode === 'advantage'
    ? Math.max(d20a, d20b!)
    : rollMode === 'disadvantage'
      ? Math.min(d20a, d20b!)
      : d20a;
  const attackFlatBonus = resolveAttackValue(attackBonus, attack);
  const damageFlatBonus = resolveAttackValue(damageBonus, attack);
  let effectiveMod = attackFlatBonus;
  let resolvedAttackComponents: any[] | undefined;

  if (attack.attackComponents?.length) {
    const { total, resolved } = resolveComponents(attack.attackComponents, char, 0, gameSystem);
    effectiveMod += total;
    resolvedAttackComponents = resolved.length ? resolved : undefined;
  } else {
    const baseAttackMod = attack.attackModSource
      ? resolveAttackModSource(parseAttackModSource(attack.attackModSource, attack.attackMod), char)
      : attack.attackMod;
    effectiveMod += baseAttackMod;
  }

  const total = d20 + effectiveMod;
  const isCrit = !attack.noCrit && d20 >= attack.critRange[0] && d20 <= attack.critRange[1];
  const effectiveDamage = (attack.damage as any[]).filter(
    (d: any) => d.diceExpr.trim() !== '' || (d.modComponents?.length ?? 0) > 0,
  );

  return {
    nodeId: attack.id,
    label:  attack.label,
    d20,
    attackMod: effectiveMod,
    total,
    vsTarget: attack.vsTarget,
    resolvedAttackComponents,
    isThreat: isCrit,
    isCrit,
    rollMode,
    advantageDie: d20b,
    damage: effectiveDamage.map((d: any) => rollDamageNode(d, damageFlatBonus, char)),
    onCritExtraDamage: (attack.onCritExtra as any[])
      .filter((d: any) => d.diceExpr.trim() !== '' || (d.modComponents?.length ?? 0) > 0)
      .map((d: any) => rollDamageNode(d, 0, char)),
    critStyle: attack.critStyle,
    weaponProperties: !attack.noCrit && attack.weaponProperties?.length ? attack.weaponProperties : undefined,
  };
}

/**
 * Resolve the pending roll after the user selects which attacks hit.
 *
 * @param pending      Output of rollPhaseA.
 * @param hitMask      Array of booleans — true = this attack hit.
 * @param charName     Character name for the summary string.
 * @param _char         Reserved for future use.
 * @param _attackBonus  Reserved for future use.
 * @param _damageBonus  Reserved for future use.
 */
export function rollPhaseC(
  pending:       PendingRollResult,
  hitMask:       boolean[],
  charName?:     string,
  _char?:        Character | null,
  _attackBonus = 0,
  _damageBonus = 0,
): CompletedRollResult {
  let totalDamage = 0;
  let hits        = 0;
  const is5e      = pending.gameSystem === 'dnd5e';

  // Accumulates non-HP damage (ability score drain, negative levels) across
  // the entire routine.  Rows with specialType set are routed here instead of
  // into totalDamage / attackDamage.
  const specialDamageTotals: Partial<Record<SpecialDamageType, number>> = {};
  function addSpecial(type: SpecialDamageType, amt: number) {
    specialDamageTotals[type] = (specialDamageTotals[type] ?? 0) + amt;
  }

  const resolvedAttacks: ResolvedAttackResult[] = pending.attackResults.map((atk, i) => {
    const isHit = hitMask[i] ?? false;
    let attackDamage = 0;  // HP damage only for this attack

    if (isHit) {
      hits++;

      for (const d of atk.damage) {
        const rowCritMult = d.critMult;

        // Compute the effective row amount (applying crit rules).
        let rowAmt: number;
        if (!atk.isCrit || rowCritMult <= 1) {
          // Normal hit, or critMult=1 suppresses multiplication.
          rowAmt = d.total;
        } else if (is5e) {
          if (atk.critStyle === 'perfect') {
            // Perfect Roll (5e): maximize the base dice, then add the rolled crit dice;
            // modifier is added once.
            rowAmt = perfectCritTotal(d);
          } else {
            // Standard 5e: double the dice only; flat modifier added once.
            rowAmt = d.rolls.reduce((s, r) => s + r, 0) * 2 + d.modifier;
          }
        } else {
          // D&D 3.5 / PF1: full expression × rowCritMult.
          rowAmt = d.total * rowCritMult;
        }

        // Route to HP total or special damage map.
        if (d.specialType) {
          addSpecial(d.specialType, rowAmt);
        } else {
          attackDamage += rowAmt;
        }
      }

      // On-crit-extra rows: only added when the attack has base damage.
      // A pure check (no damage rows) produces no damage even on a crit.
      if (atk.isCrit && atk.damage.length > 0) {
        for (const d of atk.onCritExtraDamage) {
          if (d.specialType) {
            addSpecial(d.specialType, d.total);
          } else {
            attackDamage += d.total;
          }
        }
      }
    }

    totalDamage += attackDamage;  // HP damage only
    return { ...atk, isHit, attackDamage, specialEffects: resolveWeaponSpecialEffects(atk, isHit) };
  });

  // Evaluate conditional bonus damage.
  // crit_hits = number of critical hits among confirmed hits.
  const critHits = resolvedAttacks
    .filter((a) => a.isHit && a.isCrit)
    .length;

  const conditionalDamageResults: ConditionalDamageRollResult[] =
    (pending.conditionalDamageResults ?? []).map((c) => {
      const subject = c.condition.subject === 'hits' ? hits : critHits;
      const triggered =
        c.condition.op === 'gte' ? subject >= c.condition.value :
        c.condition.op === 'lte' ? subject <= c.condition.value :
        subject === c.condition.value;

      // total = HP damage only; special rows go to specialDamageTotals.
      let total = 0;
      if (triggered) {
        for (const d of c.damage) {
          if (d.specialType) {
            addSpecial(d.specialType, d.total);
          } else {
            total += d.total;
          }
        }
        totalDamage += total;
      }
      return { ...c, triggered, total };
    });

  // Phase 5: Evaluate 'hits' type bonus attacks only.
  // 'crit_hits' type is handled interactively in the executor (bonus round flow).
  const bonusResultMap = new Map<string, any>();
  for (const ba of (pending.bonusAttackResults ?? [])) {
    bonusResultMap.set(ba.nodeId, { ...ba, triggered: false, attackResults: [] });
  }

  for (const ba of (pending.bonusAttackResults ?? [])) {
    if (ba.condition.subject !== 'hits') continue;
    const triggered =
      ba.condition.op === 'gte' ? hits >= ba.condition.value :
      ba.condition.op === 'lte' ? hits <= ba.condition.value :
      hits === ba.condition.value;

    const entry = bonusResultMap.get(ba.nodeId)!;
    entry.triggered = triggered;
    if (triggered) {
      entry.attackResults = ba.attackResults;
      for (const bonusAtk of ba.attackResults) {
        for (const d of bonusAtk.damage) {
          const rowCritMult = d.critMult;
          let rowAmt: number;
          if (!bonusAtk.isCrit || rowCritMult <= 1) {
            rowAmt = d.total;
          } else if (is5e) {
            if (bonusAtk.critStyle === 'perfect') {
              rowAmt = perfectCritTotal(d);
            } else {
              rowAmt = d.rolls.reduce((s: number, r: number) => s + r, 0) * 2 + d.modifier;
            }
          } else {
            rowAmt = d.total * rowCritMult;
          }
          if (d.specialType) {
            addSpecial(d.specialType, rowAmt);
          } else {
            totalDamage += rowAmt;
          }
        }
      }
    }
  }

  // crit_hits nodes are not resolved here — the executor rolls and confirms
  // them interactively round-by-round, then merges the results into the final
  // CompletedRollResult before dispatching to VTT / server.

  const bonusAttackResults = Array.from(bonusResultMap.values());

  const hasSpecial = Object.keys(specialDamageTotals).length > 0;
  const anyDamage = resolvedAttacks.some((a) => a.damage.length > 0)
    || conditionalDamageResults.some((c) => c.triggered && c.damage.length > 0)
    || bonusAttackResults.some((b) => b.triggered && b.attackResults.length > 0)
    || hasSpecial;
  const summary = anyDamage
    ? `${pending.routineName}: ${totalDamage} damage, ${hits}/${pending.attackResults.length} hits`
    : `${pending.routineName}: ${hits}/${pending.attackResults.length} check${pending.attackResults.length !== 1 ? 's' : ''} succeeded`;

  return {
    routineId:             pending.routineId,
    routineName:           pending.routineName,
    characterName:         charName,
    gameSystem:            pending.gameSystem,
    rollMode:              pending.rollMode,
    resolvedAttacks,
    conditionalDamageResults,
    bonusAttackResults:    bonusAttackResults.length ? bonusAttackResults : undefined,
    totalDamage,
    hits,
    totalAttacks:          pending.attackResults.length,
    summary,
    appliedModifierIds:    pending.appliedModifierIds,
    appliedModifierNames:  pending.appliedModifierNames,
    specialDamageTotals:   hasSpecial ? specialDamageTotals : undefined,
    completedAt:           Date.now(),
  };
}
